import { type FC, useCallback, useState } from "react";
import type {
	MetaAdsCityTarget,
	MetaAdsDMATarget,
	MetaAdsGeoSearchResult,
	MetaAdsRegionTarget,
} from "shared";
import { useDebounceValue } from "usehooks-ts";
import { useGeoSearch } from "../useGeoSearch";
import { useGeoTargeting } from "../useGeoTargeting";

export const CityRegionPanel: FC = () => {
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<CitySearch />
			<RegionSearch />
			<DMASearch />
		</div>
	);
};

const CitySearch: FC = () => {
	const { geo, addCity, removeCity } = useGeoTargeting();
	const [query, setQuery] = useState("");
	const [debouncedQuery] = useDebounceValue(query, 400);
	const { data: results } = useGeoSearch(debouncedQuery, "city");
	const cities = geo.cities ?? [];

	const handleSelect = useCallback(
		(r: MetaAdsGeoSearchResult) => {
			addCity({
				key: r.key,
				name: r.name,
				region: r.region ?? "",
				country: r.country_code,
			} satisfies MetaAdsCityTarget);
			setQuery("");
		},
		[addCity],
	);

	return (
		<GeoSearchSection
			label="Cities"
			placeholder="Search cities..."
			query={query}
			onQueryChange={setQuery}
			results={results?.filter((r) => r.type === "city")}
			onSelect={handleSelect}
			items={cities}
			onRemove={removeCity}
			renderItem={(c: MetaAdsCityTarget) =>
				`${c.name}${c.region ? `, ${c.region}` : ""}`
			}
		/>
	);
};

const RegionSearch: FC = () => {
	const { geo, addRegion, removeRegion } = useGeoTargeting();
	const [query, setQuery] = useState("");
	const [debouncedQuery] = useDebounceValue(query, 400);
	const { data: results } = useGeoSearch(debouncedQuery, "region");
	const regions = geo.regions ?? [];

	const handleSelect = useCallback(
		(r: MetaAdsGeoSearchResult) => {
			addRegion({
				key: r.key,
				name: r.name,
				country: r.country_code,
			} satisfies MetaAdsRegionTarget);
			setQuery("");
		},
		[addRegion],
	);

	return (
		<GeoSearchSection
			label="Regions / States"
			placeholder="Search regions..."
			query={query}
			onQueryChange={setQuery}
			results={results?.filter((r) => r.type === "region")}
			onSelect={handleSelect}
			items={regions}
			onRemove={removeRegion}
			renderItem={(r: MetaAdsRegionTarget) => `${r.name} (${r.country})`}
		/>
	);
};

const DMASearch: FC = () => {
	const { geo, addDMA, removeDMA } = useGeoTargeting();
	const [query, setQuery] = useState("");
	const [debouncedQuery] = useDebounceValue(query, 400);
	const { data: results } = useGeoSearch(debouncedQuery, "geo_market");
	const dmas = geo.dmas ?? [];

	const handleSelect = useCallback(
		(r: MetaAdsGeoSearchResult) => {
			addDMA({
				key: r.key,
				name: r.name,
			} satisfies MetaAdsDMATarget);
			setQuery("");
		},
		[addDMA],
	);

	return (
		<GeoSearchSection
			label="DMAs (Designated Market Areas)"
			placeholder="Search DMAs..."
			query={query}
			onQueryChange={setQuery}
			results={results?.filter((r) => r.type === "geo_market")}
			onSelect={handleSelect}
			items={dmas}
			onRemove={removeDMA}
			renderItem={(d: MetaAdsDMATarget) => d.name}
		/>
	);
};

interface GeoSearchSectionProps<T extends { key: string }> {
	label: string;
	placeholder: string;
	query: string;
	onQueryChange: (q: string) => void;
	results: MetaAdsGeoSearchResult[] | undefined;
	onSelect: (r: MetaAdsGeoSearchResult) => void;
	items: T[];
	onRemove: (key: string) => void;
	renderItem: (item: T) => string;
}

function GeoSearchSection<T extends { key: string }>({
	label,
	placeholder,
	query,
	onQueryChange,
	results,
	onSelect,
	items,
	onRemove,
	renderItem,
}: GeoSearchSectionProps<T>) {
	const inputId = `geo-${label.toLowerCase().replace(/\s+/g, "-")}`;
	return (
		<div>
			<label
				htmlFor={inputId}
				className="form-label"
				style={{ fontSize: 13, fontWeight: 500 }}
			>
				{label}
			</label>
			<input
				id={inputId}
				type="text"
				className="form-control form-control-sm"
				placeholder={placeholder}
				value={query}
				onChange={(e) => onQueryChange(e.target.value)}
			/>
			{query.length >= 2 && results && results.length > 0 && (
				<div
					className="list-group mt-1"
					style={{ maxHeight: 120, overflowY: "auto" }}
				>
					{results.map((r) => (
						<button
							key={r.key}
							type="button"
							className="list-group-item list-group-item-action py-1 px-2"
							style={{ fontSize: 13 }}
							onClick={() => onSelect(r)}
						>
							{r.name}
							{r.region ? ` - ${r.region}` : ""}
						</button>
					))}
				</div>
			)}
			{items.length > 0 && (
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: 4,
						marginTop: 4,
					}}
				>
					{items.map((item) => (
						<button
							type="button"
							key={item.key}
							className="badge bg-success border-0"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 4,
								cursor: "pointer",
								fontSize: 12,
							}}
							onClick={() => onRemove(item.key)}
						>
							{renderItem(item)}
							<span style={{ fontWeight: "bold" }}>&times;</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
